import { Icon } from '@chakra-ui/icons'
import { Box, Button, HStack, IconButton, useDisclosure } from '@chakra-ui/react'
import AccessAccountButton from 'components/buttons/AccessAccountButton'
import ExternalLink from 'components/links/ExternalLink'
import MedleveLogo from 'components/svg/MedleveLogo'
import MenuTwoLines from 'components/svg/MenuTwoLines'
import WhatsApp from 'components/svg/WhatsApp'
import { Label } from 'components/typography'
import { MEDLEVE_WHATSAPP_LINK } from 'constants/omensSupportContact'
import useIntersectionObserver from 'hooks/helpers/useIntersectionObserver'
import { capitalize } from 'lodash'
import Link from 'next/link'
import { FC, useEffect, useRef, useState } from 'react'
import { BsXLg } from 'react-icons/bs'
import { IoChatbubbleOutline } from 'react-icons/io5'
import { useAppSelector } from 'redux/store'

import { HOME_COMMUNITY_URL, HOME_HERO_ID } from '../constants'
import HomeMobileMenu from './HomeMobileMenu'

const CommunityButton: FC<{ compact?: boolean }> = ({ compact = false }) => (
  <Button
    as="a"
    href={HOME_COMMUNITY_URL}
    target="_blank"
    rel="noopener noreferrer"
    leftIcon={<IoChatbubbleOutline />}
    bg="#25D366"
    color="white"
    size="sm"
    borderRadius="full"
    _hover={{ bg: '#1ebe5d' }}
    {...(compact && { fontSize: 'xs', px: 3 })}
  >
    Comunidade
  </Button>
)

/**
 * Header of the organic homepage: the Medleve branch of the shared landing `Navbar`
 * (`NavbarMobile` + `NavbarDesktop`), isolated (PRD §33).
 *
 * The shared navbar picks the brand from the `isMedleveAddress` cookie and from persisted redux state,
 * neither of which exists on the server or on a first visit, and its desktop bar renders `null` until
 * mounted. This copy is Medleve by construction, so the server HTML and the first client render match.
 * Anything derived from persisted redux state (the logged-in first name) only appears after mount.
 */
const HomeHeader: FC = () => {
  const { isOpen, onToggle, onClose } = useDisclosure()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const profile = useAppSelector((state) => state.auth.profile)
  const [mounted, setMounted] = useState(false)
  const [heroSection, setHeroSection] = useState<Element | null>(null)
  const entry = useIntersectionObserver(heroSection)

  useEffect(() => {
    setMounted(true)
    setHeroSection(document.getElementById(HOME_HERO_ID))
  }, [])

  // Transparent over the hero (server default, the page loads at the top), solid once it scrolls away.
  const isSolid = entry ? !entry.isIntersecting : false
  const firstName = mounted && profile?.firstName ? capitalize(profile.firstName.split(' ')[0]) : ''

  return (
    <Box as="header" position="sticky" top="0" zIndex="sticky">
      {/* Mobile / tablet bar */}
      <Box display={{ base: 'block', lg: 'none' }} p="12px 24px" width="100%" position="absolute">
        <HStack
          as="nav"
          aria-label="Navegação principal"
          h="64px"
          justify="space-between"
          spacing={2}
          px="6"
          bg={isOpen ? 'transparent' : 'rgba(23, 23, 18, 0.25)'}
          borderColor="gray.70"
          backdropFilter={isOpen ? 'none' : 'blur(30px)'}
          borderRadius={16}
        >
          <MedleveLogo
            role="img"
            aria-label="Medleve"
            h="60px"
            w="60px"
            display={isOpen ? 'none' : 'block'}
            color="white"
          />
          {!isOpen && <CommunityButton compact />}
          <IconButton
            ref={menuButtonRef}
            aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={isOpen}
            variant="ghost"
            boxShadow="none"
            _hover={{ bg: 'gray.150' }}
            icon={<Icon fontSize="24px" stroke="alabaster" color="alabaster" as={isOpen ? BsXLg : MenuTwoLines} />}
            backdropFilter={isOpen ? 'blur(30px)' : 'none'}
            onClick={onToggle}
          />
        </HStack>
        <HomeMobileMenu isOpen={isOpen} onClose={onClose} buttonRef={menuButtonRef} />
      </Box>

      {/* Desktop bar */}
      <HStack
        as="nav"
        aria-label="Navegação principal"
        display={{ base: 'none', lg: 'flex' }}
        bg={isSolid ? 'grayscale.20' : 'transparent'}
        minH="72px"
        zIndex="100"
        width="100%"
        shadow="sm"
        justifyContent="space-between"
        transition="background-color 250ms ease-out"
        pos="absolute"
      >
        <Box
          bg={isSolid ? 'grayscale.20' : 'transparent'}
          backdropFilter="blur(30px)"
          w="full"
          h="full"
          position="absolute"
          zIndex="-1"
        />
        <Box pl="24px">
          <Link href="/" aria-label="Medleve — página inicial">
            <MedleveLogo h="80px" w="auto" color={isSolid ? 'medleve' : 'alabaster'} />
          </Link>
        </Box>

        <HStack gap="14px" pl="12px" pr="24px">
          <CommunityButton />
          <AccessAccountButton
            aria-label="Acessar conta"
            btnBg={isSolid ? 'alabaster' : 'rgba(23, 23, 18, 0.1)'}
            iconColor={isSolid ? 'darkchoc' : 'alabaster'}
          />
          <ExternalLink href={MEDLEVE_WHATSAPP_LINK}>
            <IconButton
              variant="ghost"
              bg={isSolid ? 'alabaster' : 'rgba(23, 23, 18, 0.1)'}
              shadow="none"
              aria-label="WhatsApp"
              icon={<WhatsApp fontSize="20px" color={isSolid ? 'darkchoc' : 'alabaster'} />}
              minH="48px"
              minW="48px"
            />
          </ExternalLink>
          {firstName && (
            <Link prefetch={false} href="/me">
              <Label color={isSolid ? 'phthalo' : 'alabaster'}>{firstName}</Label>
            </Link>
          )}
        </HStack>
      </HStack>
    </Box>
  )
}

export default HomeHeader
